/**
 * Inline row editing — Step 42.
 *
 * The last of the reference's queue behaviours: retriaging a batch meant
 * opening each ticket, changing one field and coming back, which is four
 * navigations per ticket to do the one thing a triage pass is made of.
 *
 * ── Why it does NOT invalidate the list ────────────────────────────────
 *
 * The plan's rule is *"never optimistically reorder or remove a row — rows
 * vanishing under the cursor is the most disorienting thing a queue can do"*.
 * A status change very often moves a ticket out of the view you changed it
 * from (that IS triage), so invalidating would delete the row you just
 * touched, shift every row below it up, and leave the next click landing on a
 * different ticket.
 *
 * So the commit writes the row IN PLACE in the cache and stops there. The
 * queue then shows a row that may no longer match its filter — deliberately,
 * and only until the next natural refetch. Showing a ticket a moment longer
 * than it belonged is the far cheaper error.
 *
 * ── Why this is not a `<select>` ───────────────────────────────────────
 *
 * It was one, and Kai: *"the dropdown UI is REALLY clunky."* Three reasons,
 * all structural rather than cosmetic:
 *
 * 1. **Two clicks to open.** One to enter edit mode, another to open the
 *    select. Halving the clicks is most of the point of editing on the row.
 * 2. **The cell's content was replaced by the control**, so the value you
 *    were about to change disappeared at the moment you went to change it,
 *    and the row visibly jolted.
 * 3. **No search.** A native select's typeahead matches only the start of an
 *    option, which is useless for a list of agents you think of by surname.
 *
 * One click now opens an anchored popover; the value stays put behind it.
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { NO_AUTOFILL } from '../../components/ui/input';
import { useAnchoredPopover } from '../../lib/useAnchoredPopover';
import { cn } from '../../lib/utils';

export interface InlineOption {
  value: string;
  label: string;
}

export interface InlineEditCtx {
  statuses: InlineOption[];
  groups: InlineOption[];
  /**
   * The agents assignable to a ticket in THIS group — not every agent.
   *
   * Kai: *"you need to only be able to select the people in that group to
   * assign a ticket to."* The server has always enforced it (a non-member is
   * a 400, and so is assigning with no group at all), so a flat list was the
   * queue offering choices the API rejects — the worst kind of picker, since
   * you only learn the option was never real after you commit to it.
   *
   * A function rather than a list because it varies per ROW: two rows in the
   * same queue routinely sit with different groups.
   */
  agentsFor: (groupId: string | null | undefined) => InlineOption[];
  /** Atomic PATCH of ONE property — the approved deviation from draft-first.
   *  A single-select holding one enumerated value can neither half-commit nor
   *  be changed by a stray keystroke, which is what draft-first guards. */
  commit: (ticketId: string, patch: Record<string, unknown>) => Promise<void>;
}

/** Above this many options the popover grows a filter box. Below it, the
 *  filter is just a thing to tab past. */
const SEARCH_THRESHOLD = 8;
const POPOVER_W = 224;
const POPOVER_MAX_H = 280;

export function InlineCell({
  value,
  options,
  display,
  onCommit,
  placeholder,
  align,
  disabledReason,
}: {
  value: string;
  options: InlineOption[];
  display: React.ReactNode;
  onCommit: (next: string) => Promise<void>;
  placeholder: string;
  align?: 'right';
  /** Why this cannot be edited right now — shown as the tooltip, and the
   *  reason the control does not open. `undefined` means editable. */
  disabledReason?: string;
}) {
  const { open, setOpen, pos, triggerRef, popRef } = useAnchoredPopover(POPOVER_W, POPOVER_MAX_H);
  const [saving, setSaving] = useState(false);
  // URL state on purpose: this is the popover's own typeahead buffer, the
  // same exception the Combobox takes. It is cleared every time the popover
  // opens and belongs to the control, not to the page — putting it in the
  // URL would put a half-typed agent surname in a shared queue link.
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  // The clear option is a real choice, not an absence: unassigning a ticket
  // or taking it off a group is half of what triage IS.
  const items: InlineOption[] = [{ value: '', label: placeholder }, ...filtered];

  const openPicker = () => {
    setQuery('');
    setActive(Math.max(0, options.findIndex((o) => o.value === value) + 1));
    setOpen(true);
    if (options.length >= SEARCH_THRESHOLD) requestAnimationFrame(() => inputRef.current?.focus());
  };

  const choose = (next: string) => {
    setOpen(false);
    if (next === value) return;
    setSaving(true);
    void onCommit(next).finally(() => setSaving(false));
  };

  const onKey = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && items[active]) {
      e.preventDefault();
      choose(items[active]!.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        // Not a `<div onClick>`: this is a control, so it gets a real button's
        // keyboard behaviour and focus ring rather than a hand-rolled imitation.
        disabled={!!disabledReason}
        onClick={(e) => {
          e.stopPropagation();
          if (open) setOpen(false);
          else openPicker();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            openPicker();
          }
        }}
        className={cn(
          'flex w-full min-w-0 items-center gap-1 rounded border border-transparent px-2 py-1 text-left',
          // ── The affordance ────────────────────────────────────────────────
          //
          // Kai: *"it needs to be more obvious what is inline editable, and
          // what isn't, either by hover or otherwise."*
          //
          // It keys off `group/row`, NOT this cell, and that is the whole
          // point. A per-cell hover tint means finding the editable fields by
          // hovering all nine of them one at a time — you can only ever see
          // the one you are already on. Hovering the ROW frames every editable
          // cell in it at once, so a single pass tells you the set, and the
          // cells that stay flat are visibly the ones that do nothing.
          //
          // A BORDER ONLY — no fill. It was `bg-background/70`, and Kai:
          // *"some weird highlighting on hover … like it's got another white
          // element behind"*. Which is exactly what it was: `--background` is
          // pure white in light mode, so a 70% fill punched a white block into
          // the row's own `bg-accent/50` hover tint. The reference's trigger
          // carries no fill and no resting border at all — its background is
          // the row's, measured off the capture.
          'group-hover/row:border-input',
          // The keyboard's equivalent of that row hover — see `data-focused`
          // on the row.
          'group-data-[focused]/row:border-input',
          // Direct hover and keyboard focus then single this one out of that
          // set — otherwise the row reveal would leave you unsure which of the
          // framed cells you are about to open.
          //
          // A RING, and ONLY a ring. Anything touching border-colour or
          // background here loses: hovering a cell also hovers its row, so
          // both rules apply, and Tailwind emits the `group-hover` variant
          // AFTER `hover` — checked in the built CSS, not assumed. A
          // `hover:bg-*` here was silently dead for exactly that reason. A
          // ring is a different property, so it stacks instead of competing.
          'hover:ring-1 hover:ring-ring',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          // Touch has no hover, so the frame is simply always on there. This
          // is the "or otherwise": without it the affordance does not exist at
          // all on a tablet, which is where a triage pass is most likely to
          // happen.
          '[@media(hover:none)]:border-input',
          open && 'border-input ring-1 ring-ring',
          saving && 'opacity-50',
          // Deliberately keeps the row-hover frame OFF when disabled: the
          // whole job of that frame is to say "this one is editable", so
          // showing it on a cell that will not open would be a lie.
          disabledReason &&
            // Only the border needs suppressing now the frame has no fill.
            'cursor-not-allowed group-hover/row:border-transparent group-data-[focused]/row:border-transparent hover:ring-0',
          align === 'right' && 'justify-end text-right',
        )}
        title={disabledReason ?? 'Click to change'}
      >
        <span className="min-w-0 truncate">{display}</span>
        {/* The caret is the literal "this opens" signal — a frame alone reads
            as a read-only box. Reserved space (`shrink-0`, never unmounted) so
            revealing it cannot re-truncate the text beside it. */}
        <ChevronDown
          aria-hidden
          className={cn(
            'h-3 w-3 shrink-0 text-muted-foreground transition-opacity',
            open ? 'opacity-100' : 'opacity-0',
            !disabledReason && 'group-hover/row:opacity-70 group-data-[focused]/row:opacity-70',
            !disabledReason && '[@media(hover:none)]:opacity-70',
          )}
        />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            role="listbox"
            style={{
              top: pos.top,
              left: pos.left,
              width: POPOVER_W,
              ...(pos.flip ? { transform: 'translateY(-100%)' } : {}),
            }}
            className="fixed z-50 overflow-hidden rounded-md border bg-popover shadow-md"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={onKey}
          >
            {options.length >= SEARCH_THRESHOLD && (
              <input
                {...NO_AUTOFILL}
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                placeholder="Type to filter…"
                className="w-full border-b bg-transparent px-2 py-1.5 text-xs focus:outline-none"
              />
            )}
            <div className="max-h-60 overflow-y-auto py-1">
              {items.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No matches.</p>}
              {items.map((o, i) => (
                <button
                  key={o.value || '__none'}
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  // `onMouseEnter` rather than hover styling alone, so the
                  // pointer and the arrow keys drive the SAME highlight —
                  // two independent ones is how you end up committing a row
                  // you were not looking at.
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(o.value)}
                  className={cn(
                    'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs',
                    i === active && 'bg-accent',
                    !o.value && 'text-muted-foreground',
                  )}
                >
                  <Check
                    className={cn('h-3 w-3 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0')}
                  />
                  <span className="min-w-0 truncate">{o.label}</span>
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
